/**
 * SMS push notification lifecycle wiring.
 *
 * Renders nothing. Mounted inside the authenticated provider tree (App.tsx,
 * inside `BrowserRouter` + `KioskProvider`). Lifecycle plumbing
 * (register-on-login + 7-day cadence, SW message subscription) comes from
 * `@aegix/web-push`; this component owns SMS's behavior:
 *
 *   - **Register on login** — `useUser()` turning non-null (password login,
 *     SSO `loginWithToken`, AND page reloads of an authenticated session)
 *     triggers registration; this is the SMS analog of AIM's
 *     `UserDataProvider` FCM effect, without touching each login call site.
 *   - **7-day re-registration** interval (token freshness / rotation).
 *   - **Silent `token_refresh` pushes** — the service worker posts
 *     `TOKEN_REFRESH` for both foreground and background deliveries (the SW
 *     is the delivery point for web push either way); this listener
 *     re-registers.
 *   - **Foreground `sms_reunification` events** — the SW forwards the push
 *     (`SMS_PUSH`) when a visible client exists instead of showing a system
 *     notification; this listener surfaces an in-app toast.
 *     `sms_message_delivery` foreground pushes are deliberately NOT toasted
 *     — the delivery-metrics UI is the in-app surface for those.
 *   - **Notification clicks** — the SW focuses the page and posts
 *     `NOTIFICATION_CLICK`; reunification events navigate to the
 *     reunification dashboard.
 *
 * Kiosk gate: SMS kiosk mode (ADR-073 — communal visitor check-in device,
 * kiosk sessions mint a scoped child user) must never prompt a hallway
 * kiosk for notification permission nor bind a per-user push token to a
 * communal device. This uses SMS's own `useKiosk()`, not a port of AIM's
 * embed-context guard.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@aegix/auth-identity';
import {
  SW_MESSAGE_TOKEN_REFRESH,
  useServiceWorkerMessages,
  useWebPushRegistrationSchedule,
} from '@aegix/web-push';
import type { SMSUser } from '../lib/sms-auth';
import { useKiosk } from '../contexts/KioskContext';
import { toaster } from '../components/ui/toaster';
import { registerSmsPush } from './registerPush';

/** Message types the service worker posts to pages (keep in sync with
 * `public/firebase-messaging-sw.js`). */
export { SW_MESSAGE_TOKEN_REFRESH };
export const SW_MESSAGE_PUSH = 'SMS_PUSH';
export const SW_MESSAGE_NOTIFICATION_CLICK = 'NOTIFICATION_CLICK';

interface SmsPushData {
  type?: string;
  title?: string;
  body?: string;
  reunification_event_id?: string;
  event_type?: string;
}

export function PushNotificationsBootstrap() {
  const user = useUser<SMSUser>();
  const { isKioskMode } = useKiosk();
  const navigate = useNavigate();

  // Stable ref so listener/interval callbacks see the current auth state
  // without re-subscribing.
  const userIdRef = useRef<number | undefined>(user?.id);
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  const handleSwMessage = useCallback(
    (event: MessageEvent) => {
      const { type, data } = (event.data ?? {}) as {
        type?: string;
        data?: SmsPushData;
      };

      if (type === SW_MESSAGE_TOKEN_REFRESH) {
        if (userIdRef.current != null) {
          void registerSmsPush();
        }
        return;
      }

      if (type === SW_MESSAGE_PUSH) {
        // Foreground delivery: in-app toast for reunification real-time
        // comms only (sms_message_delivery stays silent in-foreground).
        if (data?.type === 'sms_reunification' && data.title) {
          toaster.create({
            title: data.title,
            description: data.body,
            type: 'info',
            closable: true,
          });
        }
        return;
      }

      if (type === SW_MESSAGE_NOTIFICATION_CLICK) {
        if (data?.type === 'sms_reunification') {
          navigate('/reunification');
        }
      }
    },
    [navigate],
  );

  // Register on authenticated session + every 7 days. Kiosk mode both
  // skips registration and tears the interval down (mode can flip
  // mid-session via enter/exitKioskMode).
  useWebPushRegistrationSchedule({
    userId: user?.id,
    enabled: !isKioskMode,
    register: registerSmsPush,
  });

  // Service-worker message channel (token refresh, foreground pushes,
  // notification clicks). Subscribed while authenticated and not in kiosk
  // mode — same gate as registration.
  useServiceWorkerMessages({
    enabled: user?.id != null && !isKioskMode,
    onMessage: handleSwMessage,
  });

  return null;
}

export default PushNotificationsBootstrap;
